<script setup lang="ts">
import {
    AlertCircle,
    CheckCircle2,
    FileText,
    FileUp,
    Loader2,
    RefreshCw,
    Sparkles,
    X,
} from '@lucide/vue'
import { computed, ref } from 'vue'

import { Badge } from '@/components/ui/badge/index'
import { Button } from '@/components/ui/button/index'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog/index'
import { Separator } from '@/components/ui/separator/index'

import { useAiUpload } from '../composables/useAiUpload'

// ============================================================================
// Props & Emits
// ============================================================================

const props = defineProps<{
    open: boolean
    /** The portal-crudmanager model key (e.g. 'client') */
    modelKey: string
    /** Fully-qualified Eloquent model class */
    modelClass: string
    /** Label shown in the modal header */
    label?: string
    /** Helper text below the header */
    description?: string
    /** Accepted MIME types for the file input */
    acceptedMimeTypes?: string[]
    /** Max file size in MB */
    maxFileSizeMb?: number
    /** Allow multiple file selection */
    multiple?: boolean
}>()

const emit = defineEmits<{
    'update:open': [value: boolean]
    /** Emitted on successful completion with the queue result */
    completed: [result: Record<string, unknown>]
}>()

// ============================================================================
// State
// ============================================================================

const { queueStatus, error, uploadFiles, confirmModelType, retryProcessing, getActiveStep, reset } =
    useAiUpload()

const selectedFiles = ref<File[]>([])
const isDragOver = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)

// ============================================================================
// Computed
// ============================================================================

const acceptAttr = computed(() => (props.acceptedMimeTypes ?? ['application/pdf', 'image/jpeg', 'image/png']).join(','))

const stage = computed(() => {
    if (!queueStatus.value) {
        return selectedFiles.value.length > 0 ? 'ready' : 'idle'
    }

    const status = queueStatus.value.status
    const step = queueStatus.value.queue_step

    if (status === 'completed') {
        return 'completed'
    }
    if (status === 'failed' || status === 'cancelled') {
        return 'failed'
    }
    if (status === 'awaiting_user_action' && step === 'awaiting_model_confirmation') {
        return 'awaiting_confirmation'
    }

    return 'processing'
})

const activeStep = computed(() => getActiveStep())

const modelSuggestions = computed(() => queueStatus.value?.latest_analysis_results ?? [])

const processingSteps = [
    { key: 'analysis', label: 'Analyzing document', step: 1 },
    { key: 'extraction', label: 'Extracting data', step: 2 },
    { key: 'review', label: 'Reviewing results', step: 3 },
    { key: 'finalization', label: 'Creating record', step: 4 },
]

// ============================================================================
// Handlers
// ============================================================================

function close(): void {
    if (stage.value === 'processing') {
        return // Don't allow closing while processing
    }

    reset()
    selectedFiles.value = []
    emit('update:open', false)
}

function onDragOver(e: DragEvent): void {
    e.preventDefault()
    isDragOver.value = true
}

function onDragLeave(): void {
    isDragOver.value = false
}

function onDrop(e: DragEvent): void {
    e.preventDefault()
    isDragOver.value = false
    const files = Array.from(e.dataTransfer?.files ?? [])
    if (files.length > 0) {
        selectedFiles.value = props.multiple ? files : [files[0]]
    }
}

function onFileInput(e: Event): void {
    const input = e.target as HTMLInputElement
    const files = Array.from(input.files ?? [])
    if (files.length > 0) {
        selectedFiles.value = props.multiple ? files : [files[0]]
    }
}

function openFilePicker(): void {
    fileInputRef.value?.click()
}

function removeFile(index: number): void {
    selectedFiles.value = selectedFiles.value.filter((_, i) => i !== index)
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

async function handleProcess(): Promise<void> {
    if (selectedFiles.value.length === 0) {
        return
    }

    await uploadFiles(selectedFiles.value, props.modelKey, props.modelClass)
}

async function handleConfirm(modelType: string): Promise<void> {
    await confirmModelType(modelType)
}

async function handleRetry(): Promise<void> {
    await retryProcessing()
}

function handleCompleted(): void {
    const result = (queueStatus.value?.latest_finalize_results ?? {}) as Record<string, unknown>
    emit('completed', result)
    close()
}
</script>

<template>
    <Dialog :open="open" @update:open="close">
        <DialogContent class="sm:max-w-lg" :show-close="stage !== 'processing'">
            <DialogHeader>
                <DialogTitle class="flex items-center gap-2">
                    <Sparkles class="text-primary size-5" />
                    {{ label ?? 'Import from document' }}
                </DialogTitle>
                <DialogDescription v-if="description">
                    {{ description }}
                </DialogDescription>
            </DialogHeader>

            <!-- ── IDLE / READY: File Dropzone ─────────────────────────── -->
            <template v-if="stage === 'idle' || stage === 'ready'">
                <div
                    class="border-input hover:border-primary/50 relative cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors"
                    :class="{ 'border-primary bg-primary/5': isDragOver }"
                    @dragover="onDragOver"
                    @dragleave="onDragLeave"
                    @drop="onDrop"
                    @click="openFilePicker"
                >
                    <input
                        ref="fileInputRef"
                        type="file"
                        class="hidden"
                        :accept="acceptAttr"
                        :multiple="multiple"
                        @change="onFileInput"
                    />
                    <FileUp class="text-muted-foreground mx-auto mb-3 size-10" />
                    <p class="text-sm font-medium">Drop your file here, or click to browse</p>
                    <p class="text-muted-foreground mt-1 text-xs">
                        {{ acceptedMimeTypes?.join(', ') ?? 'PDF, JPG, PNG' }} · Max {{ maxFileSizeMb ?? 10 }}MB
                    </p>
                </div>

                <!-- Selected files list -->
                <ul v-if="selectedFiles.length > 0" class="mt-3 space-y-2">
                    <li
                        v-for="(file, i) in selectedFiles"
                        :key="i"
                        class="bg-muted flex items-center gap-3 rounded-md px-3 py-2"
                    >
                        <FileText class="text-muted-foreground size-4 shrink-0" />
                        <span class="min-w-0 flex-1 truncate text-sm">{{ file.name }}</span>
                        <span class="text-muted-foreground shrink-0 text-xs">{{ formatFileSize(file.size) }}</span>
                        <button class="text-muted-foreground hover:text-foreground shrink-0" @click.stop="removeFile(i)">
                            <X class="size-4" />
                        </button>
                    </li>
                </ul>

                <div class="mt-4 flex justify-end gap-2">
                    <Button variant="outline" @click="close">Cancel</Button>
                    <Button :disabled="selectedFiles.length === 0" @click="handleProcess">
                        Process with AI
                    </Button>
                </div>
            </template>

            <!-- ── PROCESSING: Stage progress ──────────────────────────── -->
            <template v-else-if="stage === 'processing'">
                <div class="py-2">
                    <ul class="space-y-3">
                        <li
                            v-for="step in processingSteps"
                            :key="step.key"
                            class="flex items-center gap-3"
                        >
                            <!-- Step icon -->
                            <div class="flex size-7 shrink-0 items-center justify-center rounded-full">
                                <Loader2
                                    v-if="activeStep === step.step"
                                    class="text-primary size-5 animate-spin"
                                />
                                <CheckCircle2
                                    v-else-if="activeStep > step.step"
                                    class="text-primary size-5"
                                />
                                <div
                                    v-else
                                    class="bg-muted size-5 rounded-full"
                                />
                            </div>

                            <!-- Step label -->
                            <span
                                class="text-sm"
                                :class="{
                                    'font-medium': activeStep === step.step,
                                    'text-muted-foreground': activeStep < step.step,
                                }"
                            >
                                {{ step.label }}
                            </span>

                            <!-- Status badge -->
                            <Badge
                                v-if="activeStep === step.step"
                                variant="secondary"
                                class="ml-auto text-xs"
                            >
                                Running…
                            </Badge>
                            <Badge
                                v-else-if="activeStep > step.step"
                                variant="outline"
                                class="ml-auto text-xs"
                            >
                                Done
                            </Badge>
                        </li>
                    </ul>

                    <p class="text-muted-foreground mt-4 text-center text-xs">
                        This may take a moment. Please don't close this window.
                    </p>
                </div>
            </template>

            <!-- ── AWAITING CONFIRMATION: Model type selection ─────────── -->
            <template v-else-if="stage === 'awaiting_confirmation'">
                <div>
                    <p class="text-sm">
                        The AI identified multiple possible record types. Choose the correct one to continue:
                    </p>

                    <ul class="mt-3 space-y-2">
                        <li
                            v-for="suggestion in modelSuggestions"
                            :key="suggestion.model"
                            class="border-input hover:border-primary flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
                            @click="handleConfirm(suggestion.model)"
                        >
                            <div class="min-w-0 flex-1">
                                <p class="text-sm font-medium capitalize">{{ suggestion.model.replace(/_/g, ' ') }}</p>
                                <p class="text-muted-foreground mt-0.5 text-xs">{{ suggestion.reason }}</p>
                            </div>
                            <Badge variant="secondary" class="shrink-0 text-xs">
                                {{ Math.round(suggestion.confidence * 100) }}%
                            </Badge>
                        </li>
                    </ul>

                    <Separator class="my-4" />

                    <div class="flex justify-end">
                        <Button variant="outline" @click="close">Cancel</Button>
                    </div>
                </div>
            </template>

            <!-- ── COMPLETED ───────────────────────────────────────────── -->
            <template v-else-if="stage === 'completed'">
                <div class="py-4 text-center">
                    <CheckCircle2 class="text-primary mx-auto mb-3 size-12" />
                    <p class="font-medium">Record created successfully!</p>
                    <p class="text-muted-foreground mt-1 text-sm">
                        The data has been extracted and saved.
                    </p>
                </div>

                <div class="flex justify-end gap-2">
                    <Button @click="handleCompleted">Done</Button>
                </div>
            </template>

            <!-- ── FAILED ──────────────────────────────────────────────── -->
            <template v-else-if="stage === 'failed'">
                <div class="py-4 text-center">
                    <AlertCircle class="text-destructive mx-auto mb-3 size-12" />
                    <p class="font-medium">Processing failed</p>
                    <p class="text-muted-foreground mt-1 text-sm">
                        {{ error ?? queueStatus?.processing_errors?.message ?? 'An unexpected error occurred.' }}
                    </p>
                </div>

                <div class="flex justify-end gap-2">
                    <Button variant="outline" @click="close">Close</Button>
                    <Button variant="secondary" @click="handleRetry">
                        <RefreshCw class="mr-2 size-4" />
                        Retry
                    </Button>
                </div>
            </template>
        </DialogContent>
    </Dialog>
</template>
